import React from "react";
import NewSection from "./new/section";
import TakeOffSection from "./take-off/section";
import InFlightSection from "./in-flight/section";
import CompletedSection from "./completed/section";
import CancelledSection from "./cancelled/section";
import ErrorSection from "./error/section";
import { ArcSkeleton, ArcStack } from "@src/core/ui";
import { useOrderProvider } from "@src/features/order/provider";
import {
  OrderEntity,
  OrderMissionEntity,
  OrderStatusEnum,
} from "@src/features/order/entities";
import { useDispatch, useSelector } from "react-redux";
import { isMissionOrder, isNewOrder } from "@src/features/order/utils";
import { setMapLocation } from "@src/core/controllers";
import { arcHistoryDestinationPoint, arcHistoryTakeoffPoint } from "@src/core/extensions";
import { selectOrder, setSelectedOrder } from '@src/features/order/pages/orders-page/redux';
import { ORDER_COLUMN_GRID_SX } from '@src/features/order/pages/orders-page/constants';

const missionMapCenter = (order: OrderMissionEntity) =>
  arcHistoryDestinationPoint(order) ?? arcHistoryTakeoffPoint(order);

const Sections: React.FC = () => {
  const dispatch = useDispatch();
  const { orders, isLoading } = useOrderProvider();
  const selectedOrder = useSelector(selectOrder);

  const onSelectOrder = (
    order: OrderEntity | OrderMissionEntity,
    status: OrderStatusEnum
  ) => {
    if (isNewOrder(order)) {
      dispatch(
        setMapLocation({
          latitude: Number(order.deliveryLocation.latitude),
          longitude: Number(order.deliveryLocation.longitude),
        })
      );

      dispatch(setSelectedOrder({ ...order, status }));
    }

    if (isMissionOrder(order)) {
      const center = missionMapCenter(order);

      dispatch(
        setMapLocation(
          center
            ? { latitude: center.lat, longitude: center.lng }
            : {
              latitude: Number(order.drone?.latitude),
              longitude: Number(order.drone?.longitude),
            }
        )
      );

      dispatch(setSelectedOrder({ ...order, status }));
    }

    if (
      !isNewOrder(order) &&
      !isMissionOrder(order) &&
      (order as any).orders &&
      (order as any).orders.length > 0
    ) {
      const firstOrder = (order as any).orders[0];
      if (firstOrder.deliveryLocation) {
        dispatch(
          setMapLocation({
            latitude: Number(firstOrder.deliveryLocation.latitude),
            longitude: Number(firstOrder.deliveryLocation.longitude),
          })
        );
      }
      dispatch(setSelectedOrder({ ...(order as any), status }));
    }
  };

  if (isLoading) {
    return (
      <ArcStack spacing={1} mt={1}>
        <ArcSkeleton height={40} />
        <ArcSkeleton height={40} />
        <ArcSkeleton height={40} />
        <ArcSkeleton height={40} />
        <ArcSkeleton height={40} />
        <ArcSkeleton height={40} />
      </ArcStack>
    );
  }

  return (
    <ArcStack
      my={2}
      mt={3}
      mb={0}
      sx={{
        ...ORDER_COLUMN_GRID_SX,
        height: 'calc(100vh - 135px)',
        alignItems: 'stretch',
      }}>
      <NewSection
        onSelectOrder={onSelectOrder}
        orders={orders[OrderStatusEnum.NEW] ?? []}
        selected={selectedOrder as OrderEntity | null}
        count={orders.new_count ?? 0}
      />

      <TakeOffSection
        onSelectOrder={onSelectOrder}
        orders={orders[OrderStatusEnum.READY_FOR_TAKE_OFF] ?? []}
        selected={selectedOrder as OrderMissionEntity | null}
        count={orders.ready_for_take_off_count ?? 0}
      />

      <InFlightSection 
        orders={orders[OrderStatusEnum.IN_FLIGHT] ?? []}
        selected={selectedOrder as OrderMissionEntity | null}
        count={orders.in_flight_count ?? 0}
      />

      <CompletedSection
        onSelectOrder={onSelectOrder}
        orders={orders[OrderStatusEnum.COMPLETED] ?? []}
        selected={selectedOrder as OrderMissionEntity | null}
        count={orders.completed_count ?? 0}
      />

      <CancelledSection
        onSelectOrder={onSelectOrder}
        orders={orders[OrderStatusEnum.CANCELLED] ?? []}
        selected={selectedOrder as OrderMissionEntity | null}
        count={orders.cancelled_count ?? 0}
      />

      <ErrorSection
        onSelectOrder={onSelectOrder}
        orders={orders[OrderStatusEnum.ERROR] ?? []}
        selected={selectedOrder as OrderMissionEntity | null}
        count={orders.error_count ?? 0}
      />
    </ArcStack>
  );
};

export default Sections;
